import { useEffect, useRef } from "react";
import { playClick, isMuted, playNavHover } from "@/lib/sounds";

/**
 * Wrap a page section with this and any `<button>` (or `[role="button"]`) click
 * inside the wrapped subtree fires the themed click sound.
 *
 * Iter 137 also adds a delegated `mouseenter` listener that plays the
 * navigation-pane brush sound when the mouse first moves over ANY
 * button in the subtree (skipping elements flagged `data-silent="true"`).
 *
 * Usage: <SoundGate theme="studio">…</SoundGate>
 */
export const SoundGate = ({ theme = "studio", children }) => {
  const rootRef = useRef(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const findButton = (target) => {
      let el = target;
      while (el && el !== node) {
        if (el.tagName === "BUTTON" || el.getAttribute?.("role") === "button") return el;
        el = el.parentNode;
      }
      return null;
    };

    const clickHandler = (e) => {
      if (isMuted()) return;
      const btn = findButton(e.target);
      if (!btn) return;
      if (btn.dataset?.silent === "true") return;
      playClick(theme);
    };

    // Use mouseover (bubbles) and de-dupe per element via WeakSet to
    // avoid retriggering when the cursor moves inside the same button.
    const seen = new WeakSet();
    const hoverHandler = (e) => {
      if (isMuted()) return;
      const btn = findButton(e.target);
      if (!btn) return;
      if (btn.dataset?.silent === "true") return;
      if (seen.has(btn)) {
        // already played for this button; reset after the cursor leaves.
        return;
      }
      seen.add(btn);
      playNavHover();
      const release = () => { seen.delete(btn); btn.removeEventListener("mouseleave", release); };
      btn.addEventListener("mouseleave", release);
    };

    node.addEventListener("click", clickHandler, false);
    node.addEventListener("mouseover", hoverHandler, false);
    return () => {
      node.removeEventListener("click", clickHandler, false);
      node.removeEventListener("mouseover", hoverHandler, false);
    };
  }, [theme]);

  return (
    <div ref={rootRef} style={{ display: "contents" }}>
      {children}
    </div>
  );
};

export default SoundGate;
